import React, { useState, useEffect, useContext } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { usePageCopilotPrompts } from 'areas/copilot/components/CopilotPromptsContext';

import { HeaderCell, Table, TableCell, TableHeader, TableRow } from 'common/components/Table';
import { H3, PageContent, PageHeader, PageSection } from 'common/styles/page';
import { poorColor, primaryBg, primaryColor, secondaryColor } from 'common/constants/globalstyles';
import { TrendDown, TrendUp } from 'common/components/icons';
import CategorySelect from 'common/components/CategorySelect';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
} from 'chart.js';

import {
  useApiPortalV1AssessmentsResultsListQuery,
  useGetAssessmentPeriodMetricResultsQuery,
} from 'areas/assessment-schedule/api';
import { Icon } from 'semantic-ui-react';
import { useSearchParams } from 'react-router-dom';
import ImpactPie from 'common/components/charts/ImpactPie';
import ToggleControl from 'common/components/ToggleChartControl';
import AnalyticsInsightsTwoFactor from './AnalyticsInsightsTwoFactor';
import { getSelectedAssessmentPeriodCategories, getSelectedAssessmentPeriodId } from 'common/selectors/auth';
import { getCurrentOrganizationId } from '../../../common/selectors/auth';
import { TopBarContext } from 'common/layouts/AuthLayout/TopBarContext';
import TopBarControls from 'common/layouts/AuthLayout/TopBarAssessmentPeriod';
import { calculateImpact, calculateDelta } from '../utils/AnalyticsUtils';

import { AggregateType } from 'common/constants/AggregateType';
ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTitle, Tooltip, Legend);

const AnalyticsInsights: React.FC = () => {
  const assessmentPeriodId = useSelector(getSelectedAssessmentPeriodId);

  const { data: data_by_category } = useApiPortalV1AssessmentsResultsListQuery({
    aggregateType: AggregateType.CATEGORY_OVERALL,
    assessmentPeriodId: assessmentPeriodId,
  });
  const { data: orgData } = useGetAssessmentPeriodMetricResultsQuery(
    { assessmentPeriodId: assessmentPeriodId },
    { skip: !assessmentPeriodId }
  );

  const [searchParams] = useSearchParams();
  const option = searchParams.get('option');

  const organizationId = useSelector(getCurrentOrganizationId);
  const assessmentPeriodCategories = useSelector(getSelectedAssessmentPeriodCategories);
  const { setTopBarContent } = useContext(TopBarContext);
  const { setPagePrompts, setPageInfoItems, setPageInfoTitle, setPageInfoIntro } = usePageCopilotPrompts();

  useEffect(() => {
    setTopBarContent(<TopBarControls />);
    return () => setTopBarContent(null);
  }, [setTopBarContent, organizationId]);

  useEffect(() => {
    if (option && option === 'str') setSortConfig({ key: 'overall_score', direction: 'desc' });
    else if (option && option === 'def') setSortConfig({ key: 'overall_score', direction: 'asc' });
  }, [option]);

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedCategoryName, setSelectedCategoryName] = useState<string>('Selected Category');
  const [chartType, setChartType] = useState('insights');

  useEffect(() => {
    const categoryLabel = selectedCategoryName || 'Selected Category';

    setPagePrompts([
      {
        title: 'Insights Priorities',
        displayText: `Identify the key culture drivers and priorities for ${categoryLabel}.`,
        prompt:
          `Review the Organization Insights page for ${categoryLabel} and identify the most important cultural drivers, the largest performance gaps, and which options should be prioritized. Recommend concrete steps leaders can take to address the top risks and reinforce strengths.`,
        description:
          `Use this prompt to surface high-priority insight themes and action items for ${categoryLabel}.`,
      },
      {
        title: 'Category Comparison',
        displayText: `Compare performance within ${categoryLabel}.`,
        prompt:
          `Compare the data shown for ${categoryLabel} and explain which options are outperforming or underperforming relative to the overall culture score. Include any notable outliers or trends and why they matter.`,
        description:
          `Ask Copilot to compare performance within ${categoryLabel} and highlight trends.`,
      },
      {
        title: 'Next Steps from Insights',
        displayText: `Recommend next steps based on ${categoryLabel} insights.`,
        prompt:
          `Based on the key insights for ${categoryLabel}, recommend immediate next steps for leadership. Focus on practical actions that can improve culture and engagement across the organization.`,
        description: `Ask for next-step recommendations tied to ${categoryLabel} insights.`,
      },
    ]);

    setPageInfoTitle('About Organization Insights');
    setPageInfoIntro(
      'This page highlights the factors that are driving your culture score. Use it to understand which categories matter most, which groups need attention, and how to turn insights into action.',
    );
    setPageInfoItems([
      {
        term: 'Key drivers',
        description: 'Identify the most important categories affecting your culture score.',
      },
      {
        term: 'Score gaps',
        description: 'Spot categories and groups that are falling behind the organization average.',
      },
      {
        term: 'Action focus',
        description: 'Use insights to focus leadership attention where it will have the biggest impact.',
      },
    ]);

    return () => {
      setPagePrompts([]);
      setPageInfoItems([]);
      setPageInfoTitle(undefined);
      setPageInfoIntro(undefined);
    };
  }, [setPagePrompts, setPageInfoItems, setPageInfoTitle, setPageInfoIntro, selectedCategoryName]);

  const [insightsData, setInsightsData] = useState([]);
  const [overallScore, setOverallScore] = useState<number | null>(null);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: 'category_name',
    direction: 'asc',
  });

  useEffect(() => {
    const score = orgData?.overall_score;
    setOverallScore(typeof score === 'number' ? score : null);
  }, [orgData]);

  useEffect(() => {
    if (!assessmentPeriodCategories || assessmentPeriodCategories.length === 0) return;

    const selectedCategoryStillExists = assessmentPeriodCategories.some(
      (category: any) => category.id === selectedCategory
    );
    const primaryCategory = assessmentPeriodCategories.find(
      (category: any) => category.priority === 'primary'
    );
    const defaultCategoryId = primaryCategory?.id || assessmentPeriodCategories[0]?.id || null;

    const effectiveCategoryId = !selectedCategory || !selectedCategoryStillExists
      ? defaultCategoryId
      : selectedCategory;

    setSelectedCategory(effectiveCategoryId);
    const categoryName =
      assessmentPeriodCategories.find((category: any) => category.id === effectiveCategoryId)?.name ||
      'Selected Category';
    setSelectedCategoryName(categoryName);
  }, [assessmentPeriodCategories, selectedCategory]);

  useEffect(() => {
    if (data_by_category && overallScore !== null) {
      const maxImpact = Math.max(
        ...data_by_category[0]?.result?.aggregate_scores.map((item: any) =>
          calculateImpact(calculateDelta(item.overall_score, overallScore), item.average_n)
        )
      );
      const minImpact = Math.min(
        ...data_by_category[0]?.result?.aggregate_scores.map((item: any) =>
          calculateImpact(calculateDelta(item.overall_score, overallScore), item.average_n)
        )
      );

      const filteredInsightsData = data_by_category[0]?.result?.aggregate_scores
        ?.filter((factor: { category_id: string }) => factor.category_id === selectedCategory)
        ?.map((item: any) => ({
          ...item,
          impact:
            Math.round(
              calculateImpact(calculateDelta(item.overall_score, overallScore), item.average_n) *
              10
            ) / 10,
          impact_percent:
            item.overall_score < overallScore
              ? (calculateImpact(
                calculateDelta(item.overall_score, overallScore),
                item.average_n
              ) /
                minImpact) *
              -100
              : (calculateImpact(
                calculateDelta(item.overall_score, overallScore),
                item.average_n
              ) /
                maxImpact) *
              100,
          delta: Math.round(calculateDelta(item.overall_score, overallScore) * 10) / 10,
        }));
      setInsightsData(filteredInsightsData);
    }
  }, [data_by_category, selectedCategory, overallScore]);

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = [...insightsData].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
    if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const DifferenceCell = (score: number, percentageDifference: number): JSX.Element => {
    const direction =
      percentageDifference < 0 ? 'below norm' : percentageDifference > 0 ? 'above norm' : 'at norm';

    const trendIco =
      percentageDifference < 0 ? (
        <TrendIco>{TrendDown(poorColor)}</TrendIco>
      ) : percentageDifference > 0 ? (
        <TrendIco>{TrendUp(secondaryColor)}</TrendIco>
      ) : (
        <></>
      );

    return (
      <>
        <H3>
          {score.toFixed(1)} &nbsp;
          <span
            style={{
              ...(percentageDifference < 0 && { color: poorColor }),
              ...(percentageDifference > 0 && { color: secondaryColor }),
            }}
          >
            ({trendIco}
            {`${percentageDifference.toFixed(1)}% ${direction})`}
          </span>
        </H3>
      </>
    );
  };

  const SortUp = (): JSX.Element => (
    <Icon name="caret up" style={{ margin: 0, marginTop: -8, zIndex: 10 }} id={'label'} />
  );
  const SortDown = (): JSX.Element => (
    <Icon name="caret down" style={{ margin: 0, marginTop: -8, zIndex: 10 }} id={'label'} />
  );

  return chartType === 'insights' ? (
    <PageContent>
      <PageSection>
        <PageHeader
          title="Organization Insights"
          caption="Key factors impacting culture score and areas to prioritize for improvement."
          controls={
            <>
              <CategorySelect
                categories={assessmentPeriodCategories || []}
                selectedCategory={selectedCategory}
                onCategoryChange={setSelectedCategory}
                placeholder="Select Category"
                width="250px"
              />
              <ToggleControl
                buttonLabels={[
                  { type: 'insights', icon: 'eye' },
                  { type: 'multi', icon: 'random' },
                ]}
                setChartType={setChartType}
              />
            </>
          }
        />

        <Table>
          <TableHeader>
            <HeaderCell width={4} selectable onClick={() => handleSort('option_name')}>
              Name{' '}
              {sortConfig.key === 'option_name' &&
                (sortConfig.direction === 'asc' ? SortUp() : SortDown())}
            </HeaderCell>
            <HeaderCell width={4} selectable onClick={() => handleSort('category_name')}>
              Category{' '}
              {sortConfig.key === 'category_name' &&
                (sortConfig.direction === 'asc' ? SortUp() : SortDown())}
            </HeaderCell>
            <HeaderCell width={4} selectable onClick={() => handleSort('overall_score')}>
              Average Score{' '}
              {sortConfig.key === 'overall_score' &&
                (sortConfig.direction === 'asc' ? SortUp() : SortDown())}
            </HeaderCell>
            <HeaderCell width={2} selectable justify="center" onClick={() => handleSort('average_n')}>
              Population{' '}
              {sortConfig.key === 'average_n' &&
                (sortConfig.direction === 'asc' ? SortUp() : SortDown())}
            </HeaderCell>
            <HeaderCell width={2} selectable justify="center" onClick={() => handleSort('impact')}>
              Impact{' '}
              {sortConfig.key === 'impact' &&
                (sortConfig.direction === 'asc' ? SortUp() : SortDown())}
            </HeaderCell>
          </TableHeader>
          {overallScore &&
            sortedData.map(
              (
                row: {
                  option_name: string;
                  category_name: string;
                  overall_score: number;
                  average_n: number;
                  impact_percent: number;
                  delta: number;
                },
                index: React.Key
              ) => (
                <TableRow key={index}>
                  <TableCell width={4}>
                    <InsightsText>{row.option_name}</InsightsText>
                  </TableCell>
                  <TableCell width={4} $firstcol>
                    <CategoryText>{row.category_name}</CategoryText>
                  </TableCell>
                  <TableCell width={4}>
                    <InsightsText>{DifferenceCell(row.overall_score, row.delta)}</InsightsText>
                  </TableCell>
                  <TableCell width={2} justify="center">
                    <InsightsText>{row.average_n}</InsightsText>
                  </TableCell>
                  <TableCell width={2} justify="center">
                    <InsightsText>
                      <ImpactPie value={row.impact_percent} size={40} />
                    </InsightsText>
                  </TableCell>
                </TableRow>
              )
            )}
        </Table>
      </PageSection>
    </PageContent>
  ) : (
    <AnalyticsInsightsTwoFactor />
  );
};

export default AnalyticsInsights;

const TrendIco = styled.div`
      display: inline-block;
      padding-right: 6px;
      `;

const InsightsText = styled.div`
      font-family: 'Poppins', sans-serif;
      font-size: 14px;
      font-weight: 500;
      color: #333;
      `;

const CategoryText = styled.div`
      font-weight: 500;
      padding: 4px 8px;
      border-radius: 6px;
      width: fit-content;
      color: ${primaryColor};
      background-color: ${primaryBg};
      `;

